import type { JSX } from "react";
import { useEffect, useRef } from "react";

/** The most the orbits tilt, in degrees, from the top of the page to its first viewport. */
const MAX_TILT = 12;

/**
 * The guide's identity header, moving and lit: the two thin orbital ellipses
 * behind it — the homepage's ring vocabulary (a 1px hairline ellipse at low
 * opacity, an inclination per ring, no globe, no asset) with guide-owned geometry
 * — and the pool of stage light the pointer leads.
 *
 * One component for both, because they belong to the same box and the same
 * contract. Two effects, no state: each writes a CSS custom property straight
 * onto the header, so neither can re-render the page above a fixed background
 * canvas. The header's own overflow clips both, so neither the decoration nor the
 * light can reach the post artwork below it.
 *
 * The tilt follows the document scroll through ONE rAF-coalesced listener — no
 * free-running animation loop, nothing to animate in a hidden tab, and no
 * transition to lag the scroll (the same contract MapSection's scroll-owned
 * angles use). Reduced motion keeps the geometry: these angles are the static
 * shape of the decoration, exactly as the homepage's rings are.
 *
 * The spotlight follows the pointer through a second listener on the same
 * coalescing, writing the light's centre in px measured against the header, so
 * the stylesheet's `inset: 0` gradient is anchored in the header's own
 * coordinates at any width. Whether it is painted at all is the stylesheet's
 * decision (`hover: hover`): a touch device keeps the header without a light
 * stuck where the last tap landed.
 */
export function GuideHeaderMotion(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(1, window.innerHeight)));
      el.style.setProperty("--orbit-tilt", `${(progress * MAX_TILT).toFixed(2)}deg`);
    };
    const onScroll = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // The light is painted by the header's own `::after`, so the property has to
    // live on the header: a custom property inherits down, never up.
    const host = el.closest<HTMLElement>(".atlas-guide-identity");
    if (!host) return undefined;
    let raf = 0;
    let x = 0;
    let y = 0;
    const update = (): void => {
      raf = 0;
      host.style.setProperty("--spot-x", `${x.toFixed(0)}px`);
      host.style.setProperty("--spot-y", `${y.toFixed(0)}px`);
    };
    const onMove = (event: PointerEvent): void => {
      const rect = host.getBoundingClientRect();
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
      if (!raf) raf = requestAnimationFrame(update);
    };
    host.addEventListener("pointermove", onMove);
    return () => {
      host.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="atlas-guide-orbits" ref={ref} aria-hidden="true">
      <span className="atlas-guide-orbit atlas-guide-orbit--far" />
      <span className="atlas-guide-orbit atlas-guide-orbit--near" />
    </div>
  );
}
